// Slide-over panel showing one tag's diagnosis. Read only, no feedback controls.
import React, { useState } from "react";
import { Drawer, Tooltip } from "antd";
import { CaretUpFill, Dash, ChevronDown } from "react-bootstrap-icons";
import dayjs from "dayjs";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";

const severityLabels = { high: "High", medium: "Medium", low: "Low" };

const splitLines = (value) => {
  if (!value) return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
};

const formatDate = (value) => {
  if (!value) return "-";
  return dayjs(value).format("D MMM YYYY");
};

const formatCount = (value) => {
  if (value === null || value === undefined) return "-";
  return value;
};


const renderWithCodeSpans = (value) => {
  if (!value) return "-";

  const parts = value.split("`");
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return (
        <code key={index} className="diagnosis-code">
          {part}
        </code>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

// The drawer is written for the analyst with a dropdown including other fixes
const FixList = ({ items }) => (
  <ul className="diagnosis-fix-list">
    {items.map((item, index) => (
      <li key={index}>{renderWithCodeSpans(item.line)}</li>
    ))}
  </ul>
);

const FixGroup = ({ title, items }) => {
  if (items.length === 0) return null;

  return (
    <div className="diagnosis-fix-group">
      {title ? <h5 className="diagnosis-fix-group-title">{title}</h5> : null}
      <FixList items={items} />
    </div>
  );
};


const CollapsibleFixGroup = ({ title, groups }) => {
  const [expanded, setExpanded] = useState(false);

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);
  if (total === 0) return null;

  return (
    <div className="diagnosis-fix-group">
      <button
        type="button"
        className="diagnosis-fix-group-toggle"
        onClick={() => setExpanded((previous) => !previous)}
        aria-expanded={expanded}
      >
        <span className="diagnosis-fix-group-title">{title}</span>
        <span className="diagnosis-fix-group-count">{total}</span>
        <ChevronDown
          size={12}
          className={`diagnosis-urls-caret ${expanded ? "is-open" : ""}`}
        />
      </button>
      {expanded ? (
        <div className="diagnosis-handoff-groups">
          {groups.map((group) => (
            <div key={group.owner} className="diagnosis-handoff-group">
              <h5 className="diagnosis-handoff-owner">{group.owner}</h5>
              <FixList items={group.items} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const DiagnosisCard = ({ title, action, children }) => (
  <div className="diagnosis-card">
    <div className="diagnosis-card-head">
      <h4 className="diagnosis-card-title">{title}</h4>
      {action}
    </div>
    {children}
  </div>
);

// Copies the card's fixes as plain text, including the collapsed hand-off ones.
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : "Copy"}>
      <button
        type="button"
        className="diagnosis-copy-button"
        onClick={handleCopy}
        aria-label="Copy potential fixes"
      >
        {copied ? <CheckOutlined /> : <CopyOutlined />}
      </button>
    </Tooltip>
  );
};

const FooterStat = ({ label, value, onClick, expanded }) => {
  if (!onClick) {
    return (
      <div className="diagnosis-footer-stat">
        <span className="diagnosis-footer-label">{label}</span>
        <span className="diagnosis-footer-value">{value}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="diagnosis-footer-stat diagnosis-footer-stat-button"
      onClick={onClick}
      aria-expanded={expanded}
    >
      <span className="diagnosis-footer-label">{label}</span>
      <span className="diagnosis-footer-value">
        {value}
        <ChevronDown
          size={12}
          className={`diagnosis-urls-caret ${expanded ? "is-open" : ""}`}
        />
      </span>
    </button>
  );
};

const TagDiagnosticsDrawer = ({ diagnosis, open, onClose }) => {
  const [showUrls, setShowUrls] = useState(false);

  if (!diagnosis) return null;

  const severity = diagnosis.severity || "low";
  const severityLabel = severityLabels[severity] || "Low";
  const isRegression = diagnosis.trend === "regression";

  const ownFixes = splitLines(diagnosis.fixes_analysts).map((line) => ({
    line,
  }));
  
  const handoffGroups = [
    { owner: "Developer", items: splitLines(diagnosis.fixes_developers) },
    { owner: "Marketer", items: splitLines(diagnosis.fixes_marketers) },
  ]
    .filter((group) => group.items.length > 0)
    .map((group) => ({
      owner: group.owner,
      items: group.items.map((line) => ({ line })),
    }));

  const hasHandoff = handoffGroups.length > 0;
  const hasFixes = ownFixes.length > 0 || hasHandoff;
  const ownFixesTitle = hasHandoff ? "Analysts' Actionables" : null;

  const fixesText = [
    ...(ownFixes.length > 0
      ? [ownFixesTitle || "Potential fixes", ...ownFixes.map((fix) => `- ${fix.line}`)]
      : []),
    ...(hasHandoff
      ? [
          "",
          "Escalate / hand off",
          ...handoffGroups.flatMap((group) => [
            "",
            group.owner,
            ...group.items.map((fix) => `- ${fix.line}`),
          ]),
        ]
      : []),
  ].join("\n");

  const sampleUrls = splitLines(diagnosis.sample_urls);
  const affectedCount = diagnosis.affected_urls_count || 0;
  const urlCaption =
    sampleUrls.length >= affectedCount
      ? `All ${affectedCount} affected URLs`
      : `${sampleUrls.length} examples of ${affectedCount} affected URLs`;

  const header = (
    <div className="diagnosis-header">
      <h2 className="diagnosis-title">Tag Debug Assistant</h2>
      <h3 className="diagnosis-tag-name">
        {diagnosis.tag_name || diagnosis.tag_id}
      </h3>
      <div className="diagnosis-header-badges">
        <span className={`diagnosis-badge diagnosis-badge-${severity}`}>
          <span className="diagnosis-dot" />
          {severityLabel} severity
        </span>
        <span className="diagnosis-trend">
          {isRegression ? <CaretUpFill size={11} /> : <Dash size={16} />}
          {isRegression ? "recent regression" : "long-running"}
        </span>
        <span className="diagnosis-container">· {diagnosis.container_id}</span>
      </div>
      <span className="diagnosis-caption">
        Modeled from your historical tag data
      </span>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      destroyOnClose
      title={header}
      className="tag-diagnostics-drawer"
      // Width comes from styles.scss, which has to override an app-wide rule.
      rootClassName={`tag-diagnostics-drawer-root diagnosis-accent-${severity}`}
    >
      <DiagnosisCard title="Plausible root cause">
        <p className="diagnosis-headline">
          {renderWithCodeSpans(diagnosis.root_cause)}
        </p>
      </DiagnosisCard>

      <DiagnosisCard title="Possible business impact">
        <p className="diagnosis-text">
          {renderWithCodeSpans(diagnosis.business_impact)}
        </p>
      </DiagnosisCard>

      <DiagnosisCard title="Analysis">
        <p className="diagnosis-text">
          {renderWithCodeSpans(diagnosis.analysis)}
        </p>
      </DiagnosisCard>

      <DiagnosisCard
        title="Potential fixes"
        action={hasFixes ? <CopyButton text={fixesText} /> : null}
      >
        {hasFixes ? (
          <>
            <p className="diagnosis-fix-note">Guidance</p>
            <FixGroup title={ownFixesTitle} items={ownFixes} />
            <CollapsibleFixGroup
              title="Escalate / hand off"
              groups={handoffGroups}
            />
          </>
        ) : (
          <p className="diagnosis-empty">Nothing to change on this tag.</p>
        )}
      </DiagnosisCard>

      <div className="diagnosis-footer">
        <div className="diagnosis-footer-stats">
          <FooterStat
            label="Occurrences"
            value={formatCount(diagnosis.total_occurrences)}
          />
          <FooterStat
            label="Affected URLs"
            value={formatCount(diagnosis.affected_urls_count)}
            onClick={
              sampleUrls.length > 0
                ? () => setShowUrls((previous) => !previous)
                : null
            }
            expanded={showUrls}
          />
          <FooterStat
            label="Last 24h"
            value={formatCount(diagnosis.recent_24h)}
          />
        </div>
        {showUrls && sampleUrls.length > 0 ? (
          <div className="diagnosis-urls">
            <p className="diagnosis-urls-caption">{urlCaption}</p>
            <ul className="diagnosis-urls-list">
              {sampleUrls.map((url) => (
                <li key={url}>
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="diagnosis-footer-meta">
          Seen {formatDate(diagnosis.first_seen)} – {formatDate(diagnosis.last_seen)}
          <span className="diagnosis-footer-sep">·</span>
          Diagnosis run {formatDate(diagnosis.run_timestamp)}
        </p>
      </div>
    </Drawer>
  );
};

export default TagDiagnosticsDrawer;
